import AbstractReelBehaviour from "./AbstractReelBehaviour";
import Reel from "../views/Reel";
import { TimelineMax, Power2, TweenMax } from "gsap";
import GameEvents from "../const/GameEvents";
import SoundController from "../utils/SoundController";
import SoundVars from "../const/SoundVars";
import GameModel from "../model/GameModel";
import SpinModel from "../model/SpinModel";
import ReelSymbol from "../views/Symbol";
import ConsoleUtil from '../utils/ConsoleUtil';
import { IOverlayData, OverlayType } from "../server/data/ISpinData";

class ReelReverseCascadeBehaviour extends AbstractReelBehaviour {
    public cascadeTL: TimelineMax;
    public symbolsDefaultPosition: number[];
    public symbolsFromNextGen: any[];
    public symPool: ReelSymbol[];
    public inCascade: boolean = false;
    public symbolAnimTime: number = 0;
    private isStopPlaying: boolean = false;
    public animationStartDelay: number = 0;
    protected ts: number = 1;

    constructor(reel: Reel, sortFunction: () => void) {
        super(reel, sortFunction);
        this.symbolsDefaultPosition = [];
        this.symPool = [];
        this.symbolAnimTime = GameModel.gameConfig.symbolAnimTime;
        this.getSymbolsDefaultPosition();
    }

    // Override to change direction
    public get startSymbolsTweenPosition(): number {
        return this.reel.reelHeight + this.reel.symbols[0].height;

    };

    // Override to change direction
    public get endSymbolsTweenPosition(): number {
        return this.reel.yOrigin - this.reel.symbols[0].height;
    }

    // Override to change direction
    protected getNextFromPool(): ReelSymbol {
        return this.symPool.shift();
    }

    // Override to change direction
    protected getFillPosition():number {
        return this.symbolsDefaultPosition[this.symbolsDefaultPosition.length - this.symPool.length ];
    }
    
    // Override to change direction
    protected calculateSymAnimDuration(sym: ReelSymbol, targetPos: number,origDuration:number): number {
        let distance = Math.abs(targetPos - sym.y);
        let origDistance = Math.abs(this.startSymbolsTweenPosition - this.reel.yOrigin);
        let time = ((origDuration) / origDistance) * distance;

        return time;
    }

    // Override to change direction
    protected get startSymbolIndex(): number {
        return 0;
    }

    // Override to change direction
    protected getNextSymbolIndex(currentIndex: number): number {
        return currentIndex + 1;
    }

    // Override to change direction
    protected calculatePreviousExplodes(symIndex: number, explodedItems: boolean[]): number {
        let explodesBefore = 0;
        for (let index = 0; index < symIndex; index++) {
            let exploded = explodedItems[index];
            if (exploded) {
                explodesBefore++;
            }
        }

        return explodesBefore;
    }

    
    // Override to change direction
    protected calculatePreviousLocks(symIndex: number, lockedItems: number[],explodedItems: boolean[]): number {

        //TODO - properly test when possible
        if(!lockedItems) {
            return 0;
        }
        let explodesBefore = 0;
        for (let index = 0; index < symIndex; index++) {
            let isLocked = this.isLocked(index,lockedItems);
            if (isLocked) {
                if( this.calculatePreviousExplodes(index,explodedItems)>0) {
                    explodesBefore++;
                }
            }
        }

        return explodesBefore;
    }

    // Override to change direction
    protected calculateSymbolDrop(sym: ReelSymbol, symIndex: number, explodesBefore: number, lockedBefore: number, locked: boolean): number {
        let targetY = sym.y;

        if (locked) {
            return targetY;
        }
        else if (explodesBefore) {
            targetY = this.symbolsDefaultPosition[symIndex - explodesBefore - lockedBefore];
        }
        return targetY;
    }

    // Override to change direction
    protected sendSymToPool(sym: ReelSymbol): number {
        this.positionInPool(sym);
        this.symPool.push(sym);
        return sym.y;
    }

    // Override to change direction
    protected positionInPool(sym: ReelSymbol) {
        sym.y = this.startSymbolsTweenPosition + sym.height * (this.symPool.length );
        sym.visible = false;
    }

    public getSymbolsDefaultPosition() {

        const numSyms = this.reel.symbols.length;
        for (let symIdx = 0; symIdx < numSyms; symIdx++) {
            let sym = this.reel.symbols[symIdx];
            this.symbolsDefaultPosition.push(sym.y);
        }
    }

    // Use this to change ease for separate stages ( instead of overwriting functions)
    public changeEase(stopTime: number, easingFunction: gsap.Ease = Power2.easeInOut) {
        this.stopTime = stopTime || 0.8;
        this.maxStopTime = stopTime * 2;
        this.easingFunction = easingFunction;
    }

    protected killTimeline(){
        if(this.cascadeTL ) {
            this.cascadeTL.kill();
            this.cascadeTL.clear();
            this.cascadeTL = null;
        }
    }

    public spin(easingFunction: gsap.Ease = Power2.easeInOut) {
        ConsoleUtil.log("CASCADE: SPIN", this.reel.reelIndex);
        this.easingFunction = easingFunction;
        this.reel.sortSymbolsByY();
        this.inCascade = false;
       this.killTimeline();
        this.cascadeTL = new TimelineMax({
            timeScale: 1,
            paused: true,
            onComplete: this.onCompleteAnim,
            onUpdate: () => this.spinUpdate(),
            onCompleteScope: this
        });
        this.isStopPlaying = false;
        if (this.isSpinning === false) {
            this.isSpinning = true;
            this.cascadeTL.timeScale(1);
            this.primarySymbolsAnimation(false);
        }
    }

    public stop(stopSymbolData: any[], hasAnticipation: boolean) {
        ConsoleUtil.log("CASCADE: STOP", this.reel.reelIndex, this.inCascade);
        
        if (!this.isStopPlaying) {
            this.killTimeline();
            this.cascadeTL = new TimelineMax({
                paused:true,
                timeScale: 1,
                onComplete: this.onStopCompleteAnim,
                onUpdate: () => this.spinUpdate(),
                onCompleteScope: this
            });
            window["cascadeTl"+this.reel.reelIndex] = this.cascadeTL;
            this.isStopPlaying = true;
            this.stopSymbols = stopSymbolData;
            this.secondaryCascadeAnimation(hasAnticipation);
            this.isSpinning = false;
        }

    }

    public spinUpdate() {
        if (GameModel.turboSpin === true && this.ts == 1) {
            this.ts = 3;
            this.activateQuickStop();
        }
    }

    public activateQuickStop() {
        this.cascadeTL.timeScale(3);
    }

    // First half of cascade
    private primarySymbolsAnimation(hasAnticipation: boolean) {

        const lockedOverlay = SpinModel.getOverlay(OverlayType.Locked);
        const explodesMap = SpinModel.getCurrentCascadeExplodeMap();
        const explodedItems =
            explodesMap.length > 0 ? explodesMap[this.reel.reelIndex] : [];
        if (explodedItems && explodedItems.length) {
            this.fillExplodes(explodedItems,lockedOverlay?lockedOverlay.symbolPosition[this.reel.reelIndex]:null);
        } else {
            this.mainDrop(hasAnticipation);
        }

    }

    // Second half of cascade
    protected secondaryCascadeAnimation(hasAnticipation: boolean) {
        this.setFinalSymbols();
        if (!this.inCascade) {
            this.mainDrop(hasAnticipation);
        } else {
            this.fillRemaining(hasAnticipation);
        }
    }

    protected fillExplodes(explodedItems: boolean[],lockedOverlay?:number[]) {
        this.cascadeTL.pause();
        this.reel.sortSymbolsByY();
        this.inCascade = true;

        let index = this.startSymbolIndex;

        let totalDuration = 0;

        while (index >= 0 && index < this.reel.symbols.length) {
            let exploded = explodedItems[index];
            let sym = this.reel.symbols[index];
            let targetY = exploded 
            ?this.sendSymToPool(sym)
            :this.calculateSymbolDrop(
                sym, 
                index, 
                this.calculatePreviousExplodes(index, explodedItems), 
                this.calculatePreviousLocks(index,lockedOverlay,explodedItems),
                this.isLocked(index,lockedOverlay)
                );
            let duration = this.calculateSymAnimDuration(sym, targetY,GameModel.gameConfig.symbolCascadeAnimTime);
            totalDuration += duration;
            if (duration) {

                this.cascadeTL.to(sym, duration, {
                    y: targetY,
                    ease: this.easingFunction,
                    onCompleteScope: this
                },
                    "-=" + this.calculateSymAnimDuration(sym, targetY,GameModel.gameConfig.symCascadeTimeOffset));

            }
            index = this.getNextSymbolIndex(index);
        }

        if (totalDuration) {

            this.cascadeTL.restart();
        } else {
            this.onCompleteAnim();
        }

    }

    protected fillRemaining(hasAnticipation: boolean) {
        let exploded = this.symPool.length;
        this.inCascade = false;
        this.cascadeTL.pause();
        if (exploded) {
            while (this.symPool.length) {
                let targetY = this.getFillPosition();
                let sym = this.getNextFromPool();
                let duration = this.calculateSymAnimDuration(sym, targetY,GameModel.gameConfig.symbolCascadeAnimTime);
                sym.visible = true;

                this.cascadeTL.to(sym, duration, {
                    onComplete: () => {
                        this.symCascadeComplete(hasAnticipation);
                    },
                    y: targetY,
                    ease: this.easingFunction,
                    onCompleteScope: this,

                },
                    hasAnticipation
                        ? "+=" + this.calculateSymAnimDuration(sym, targetY,GameModel.gameConfig.anticipationDelay)
                        : "-=" + this.calculateSymAnimDuration(sym, targetY,GameModel.gameConfig.symCascadeTimeOffset)
                );
            }
            this.cascadeTL.restart();
        } else {
            this.onStopCompleteAnim();
        }

    }

    protected mainDrop(hasAnticipation: boolean) {
        this.cascadeTL.pause();
        const numSyms: number = this.startSymbolIndex;;
        let symIdx = this.startSymbolIndex;

        while (symIdx >= 0 && symIdx < this.reel.symbols.length) {
            let sym = this.reel.symbols[symIdx];
            let targetY = 0;
            if (this.isStopPlaying === true) {
                let symId = this.stopSymbols[symIdx];
                sym.setId(symId);
                sym.y = this.startSymbolsTweenPosition;
                targetY = this.symbolsDefaultPosition[symIdx];
            } else {
                targetY = this.endSymbolsTweenPosition;
            }
            this.cascadeTL.pause();
            if (symIdx != numSyms) {
                this.cascadeTL.to(sym, this.symbolAnimTime, {
                    y: targetY,
                    ease: this.easingFunction,
                    onComplete: () => {
                        this.symCascadeComplete(hasAnticipation);
                    },
                    onCompleteScope: this
                }, "-=" + GameModel.gameConfig.symCascadeTimeOffset);
            } else {
                this.cascadeTL.to(sym, this.symbolAnimTime, {
                    delay: this.animationStartDelay,
                    y: targetY,
                    ease: this.easingFunction,
                    onComplete: () => {
                        this.symCascadeComplete(hasAnticipation);
                    },
                    onCompleteScope: this
                },
                    hasAnticipation
                        ? "+=" + GameModel.gameConfig.anticipationDelay
                        : "-=" + GameModel.gameConfig.symCascadeTimeOffset
                );
            }
            symIdx = this.getNextSymbolIndex(symIdx);
        }
        if (hasAnticipation) {
            this.cascadeTL.add(() => {
                SoundController.playSound("anticipation");
            })
        }
        this.cascadeTL.restart();
    }

    public onCompleteAnim() {
        this.reel.emit(GameEvents.CASCADE_STOPPED, this.reel.reelIndex);
    }

    public onStopCompleteAnim() {
        if (this.isStopPlaying === true) {
            this.isSpinning = false;
            this.cascadeTL.timeScale(1);
            this.setFinalSymbols();
            this.ts = 1;
            GameModel.turboSpin = false;
            SoundController.playSound(SoundVars.EFFECT_REEL_STOP);
            this.reel.emit(GameEvents.REEL_STOPPED, this.reel);
        }
    }

    protected symCascadeComplete(hasAnticipation) {
        if (hasAnticipation) {
            SoundController.playSound(SoundVars.EFFECT_CASCADE_FALL_SECONDARY);
        }
    }

    protected setFinalSymbols() {
        this.reel.sortSymbolsByY();
        this.stopSymbols.forEach((sym, index) => {
            this.reel.symbols[index].setId(sym);
        })
    }

    protected isLocked(index:number, lockedItems:number[]):boolean {
        if(!lockedItems){
            return false;
        }
        return GameModel.gameConfig.symbolIds.indexOf(lockedItems[index])!=-1;
    }

}

export default ReelReverseCascadeBehaviour;
